import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Form,
  Button,
  Input,
  Label,
  FormFeedback,
  FormGroup,
} from 'reactstrap';
import { createOrUpdateRole } from '../../../actions/organization';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InnerLoader from '../../../Components/Common/InnerLoader';
import ReactSelect from 'react-select';
import { useSelector } from 'react-redux';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Name is required'),
});

const UserRoleForm = ({
  handleClose = () => {},
  updateHandler = () => {},
  isFromPopup = false,
  subject = 'default',
  permissions = [],
  role = null,
  brandId = null,
  canManageRole = false,
}) => {
  const { organizationTree } = useSelector((state) => state?.organization);

  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      selectedPermissions: [],
    },
    validationSchema: validationSchema.concat(
      Yup.object().shape({
        selectedPermissions: Yup.array()
          .min(1, 'At least one permission is required')
          .required('Permissions are required'),
      })
    ),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const data = {
          input: {
            roleId: values?.roleId,
            organizationId: organizationTree?.organizationId,
            brandId: brandId,
            name: values?.name,
            description: values?.description,
            subject: subject,
            permissions: values?.selectedPermissions.map((p) => ({
              permissionId: p.permissionId,
              name: p.name,
              action: p.action,
              subject: p.subject,
              conditions: p.conditions,
            })),
          },
        };

        const res = await createOrUpdateRole(data);

        if (res?.roleId) {
          alertSuccess('Updated successfully');
          if (isFromPopup) {
            setTimeout(() => {
              resetFormValues();
              updateHandler();
              handleClose();
            }, 1000);
          }
        } else {
          alertError('Failed, Please try again later');
        }
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (e) {
        console.log(e);
        setLoading(false);
        alertError('Failed, Please try again later');
      }
    },
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = formik;

  useEffect(() => {
    if (role) {
      setFieldValue('roleId', role?.roleId);
      setFieldValue('name', role?.name);
      setFieldValue('description', role?.description);
      setFieldValue(
        'selectedPermissions',
        role?.permissions?.map((perm) => ({
          permissionId: perm.permissionId,
          value: perm.permissionId,
          label: `${perm.name} ${perm.subject}`,
          name: perm.name,
          action: perm.action,
          subject: perm.subject,
          conditions: perm.conditions,
        }))
      );
    }
  }, [role]);

  const resetFormValues = () => {
    resetForm({
      values: {
        name: '',
        description: '',
        selectedPermissions: [],
      },
    });
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="name">Name</Label>
          <Input
            type="text"
            name="name"
            id="name"
            {...getFieldProps('name')}
            invalid={touched?.name && !!errors?.name}
            disabled={!canManageRole}
          />
          <FormFeedback>{errors?.name}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="permissions">Permissions</Label>
          <ReactSelect
            id="permissions"
            name="selectedPermissions"
            options={permissions?.map((perm) => ({
              permissionId: perm?.permissionId,
              value: perm?.permissionId,
              label: `${perm?.name} ${perm?.subject}`,
              name: perm?.name,
              action: perm?.action,
              subject: perm?.subject,
              conditions: perm?.conditions,
            }))}
            isMulti
            onChange={(selected) =>
              setFieldValue('selectedPermissions', selected)
            }
            onBlur={() => setFieldTouched('selectedPermissions', true)}
            value={values?.selectedPermissions}
            className="text-capitalize"
            isDisabled={!canManageRole}
          />
          {touched?.selectedPermissions && errors?.selectedPermissions && (
            <div className="invalid-feedback d-block">
              {errors?.selectedPermissions}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="description">Description</Label>
          <Input
            type="textarea"
            name="description"
            id="description"
            {...getFieldProps('description')}
            invalid={touched?.description && !!errors?.description}
            disabled={!canManageRole}
          />
          <FormFeedback>{errors?.description}</FormFeedback>
        </FormGroup>

        {canManageRole ? (
          <Button type="submit" color="primary">
            Submit
          </Button>
        ) : null}
      </Form>
      {/* <ToastContainer /> */}

      {loading && <InnerLoader />}
    </>
  );
};

export default UserRoleForm;
